/* eslint-disable */
import { useMemo } from "react";
// AUD-06 (gate J): "overdue" is the one rule in utils/devOverdue — no dev_settings.overdueRules here any more.
import { isDevOverdue } from "../utils/devOverdue";
import { compareDevNumbers } from "../utils/devNumberSort";
import { getShapes } from "../utils/devCardUtils";
// Conv #61 — _setMatch / _factoryMatch / _seasonMatch and the decision+procurement
// block used to be inline closures here. They were LIFTED into devStatusFilter (the
// single source of truth) when the designer card grew its own filter bar, so both
// surfaces resolve to the same symbols and cannot drift. No local copies remain.
import {
  matchesStatusSet, matchesSunOptic, matchesValueSet, matchesValueList,
  matchesFactorySet, matchesSeasonSet, matchesSelectionSets,
} from "../utils/devStatusFilter";

// Structured search field-list. Replaces the previous serialize-and-search
// pattern that ran on every dev on every keystroke (~1,200 devs × full
// payload = 100ms+ on the render hot path). Lower-casing happens once
// per field per dev; we short-circuit on the first match.
//
// Shape values flow through getShapes() per CLAUDE.md convention #29
// (reads shape + shape1/2/3, both top-level and nested in data, dedupes
// via Set). Do NOT inline shape1/2/3 reads here.
export function devMatchesSearch(d, q) {
  if (!q) return true;
  const data = d.data || {};
  const fields = [
    d.dev_number,    data.dev_number,
    d.designer,      data.designer,
    d.fty,           data.fty,
    d.material,      data.material,
    d.notes,         data.notes,
    d.color,         data.color,
    d.basedOn,       data.basedOn,
    // Brief 8 — brand_name lives nested at data.based_on_data.brand_name.
    // Distinct from data.basedOn (which is the TYPE enum like "Brand Image
    // / Reference"). Layer 2 (projection) already brings based_on_data
    // wholesale per devSelect.js:33 so the field arrives client-side.
    d.based_on_data?.brand_name, data.based_on_data?.brand_name,
    d.developmentOrder, data.developmentOrder,
    d.do_number,     data.do_number,        // Brief 7 (do_number — Brief N field)
    d.full_status,   data.full_status,
  ];
  for (let i = 0; i < fields.length; i++) {
    const v = fields[i];
    if (v && String(v).toLowerCase().includes(q)) return true;
  }
  // Shape: getShapes reads all 4 keys (shape, shape1, shape2, shape3)
  // from both top-level and nested data, dedupes via Set.
  const shapes = getShapes(d);
  for (let i = 0; i < shapes.length; i++) {
    if (String(shapes[i]).toLowerCase().includes(q)) return true;
  }
  return false;
}

// ── useDevFilters — pure derivation of filtered/sorted dev list + metadata ──
export function useDevFilters({ devs, search, externalFilters, filters, externalSort, isAdmin, myDesignerNames }) {
  const { designers, factories, years } = useMemo(() => ({
    designers: [...new Set(devs.map(d=>d.designer).filter(Boolean))].sort(),
    factories: [...new Set(devs.map(d=>d.fty).filter(Boolean))].sort(),
    years: [...new Set(devs.map(d=>d.year).filter(Boolean))].sort().reverse(),
  }), [devs]);

  // Preserve sort-triggered clock reads when overdue affects membership.
  const overdueSort = filters.overdue === "yes" ? externalSort : null;
  const { unsortedFiltered, filteredIgnoringStatus } = useMemo(() => {
    // Thin local aliases onto the shared predicates so the call sites below read
    // exactly as they did before the lift (same names, same arity).
    const _fMatch = matchesValueList;
    const _setMatch = matchesValueSet;
    const _factoryMatch = matchesFactorySet;
    const _seasonMatch = matchesSeasonSet;

    const searchLower = search ? search.toLowerCase() : "";

    // Predicate that applies EVERYTHING except the externalFilters.status branch.
    // Used twice: once to build `filteredIgnoringStatus` (consumed by
    // DevStatusBar for "ignoring this pill" math) and once via `passesStatus`
    // composition for the canonical `filtered` list.
    const passesNonStatus = (d) => {
      if (d.archived || d.data?.archived) return false;
      if (!isAdmin && !myDesignerNames.includes((d.designer||"").toLowerCase())) return false;
      if (searchLower && !devMatchesSearch(d, searchLower)) return false;
      if (externalFilters) {
        if (!_setMatch(externalFilters.designer,  d.designer||d.data?.designer))        return false;
        if (!_setMatch(externalFilters.type,      d.dev_type||d.data?.dev_type))        return false;
        // Brief F.1 — sunOptic derived filter on dev_type. Placed before the
        // remaining cheaper Set checks because it short-circuits on a single
        // string comparison and is expected to be the dominant filter in F.2's
        // drawer UI. Predicate handles its own empty-Set pass-through.
        if (!matchesSunOptic(d, externalFilters.sunOptic))                              return false;
        if (!_setMatch(externalFilters.material,  d.material||d.data?.material))        return false;
        if (!_setMatch(externalFilters.shape,     d.shape||d.data?.shape))              return false;
        // Task #4 — Season (data.season full strings + _no_season sentinel).
        if (!_seasonMatch(externalFilters.season, d.season||d.data?.season))            return false;
        if (!_factoryMatch(externalFilters.factory, d.fty||d.data?.fty))                return false;
        // Task 4 — Selection Decision + Procurement (client mirror of the
        // server predicate in useDevsPaginated; same FIXED season). Devs with no
        // decision for that season are excluded when either group is active.
        if (!matchesSelectionSets(d, externalFilters.decision, externalFilters.procurement)) return false;
      }
      // Internal filters (array-based, kept for advanced filtering)
      if (!_fMatch(filters.designer,   d.designer||d.data?.designer))     return false;
      if (!_factoryMatch(filters.fty, d.fty||d.data?.fty))                return false;
      if (!_fMatch(filters.dev_type,   d.dev_type||d.data?.dev_type))     return false;
      if (!_fMatch(filters.material,   d.material||d.data?.material))     return false;
      if (!_fMatch(filters.stage_status,d.stage_status||d.data?.stage_status)) return false;
      if (!_fMatch(filters.season,     d.season||d.data?.season))         return false;
      if (!_fMatch(filters.year,       d.year||d.data?.year))             return false;
      if (filters.ordered === "yes" && !d.ordered) return false;
      if (filters.ordered === "no" && d.ordered) return false;
      if (filters.overdue === "yes" && !isDevOverdue(d)) return false;
      if (!_fMatch(filters.shape,      d.shape||d.data?.shape))           return false;
      if (!_fMatch(filters.full_status,d.full_status||d.data?.full_status)) return false;
      return true;
    };

    const filteredIgnoringStatus = devs.filter(passesNonStatus);
    const statusSet = externalFilters?.status;
    const unsortedFiltered = (statusSet && statusSet.size > 0)
      ? filteredIgnoringStatus.filter(d => matchesStatusSet(d, statusSet))
      : filteredIgnoringStatus;

    return { unsortedFiltered, filteredIgnoringStatus };
  }, [devs, search, externalFilters, filters, isAdmin, myDesignerNames, overdueSort]);

  const { filtered, overdueCount } = useMemo(() => {
    let filtered = unsortedFiltered;
    // Apply external sort
    if (externalSort && externalSort !== "newest") {
      filtered = [...filtered].sort((a,b) => {
        switch(externalSort) {
          case "oldest": return new Date(a.created_at||0) - new Date(b.created_at||0);
          case "designer_az": return (a.designer||"").localeCompare(b.designer||"");
          case "type_az": return (a.dev_type||"").localeCompare(b.dev_type||"");
          case "updated": return new Date(b.updated_at||b.created_at||0) - new Date(a.updated_at||a.created_at||0);
          case "devnum_az": return compareDevNumbers(a.dev_number||a.data?.dev_number, b.dev_number||b.data?.dev_number);
          default: return 0;
        }
      });
    }

    const overdueCount = filtered.filter(d => isDevOverdue(d)).length;

    return { filtered, overdueCount };
  }, [unsortedFiltered, externalSort]);

  return useMemo(() => ({ filtered, filteredIgnoringStatus, designers, factories, years, overdueCount }),
    [filtered, filteredIgnoringStatus, designers, factories, years, overdueCount]);
}
